import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for managing async filter options with caching
 * 
 * This hook provides:
 * - Caching of filter options to avoid redundant API calls
 * - Cache invalidation when filters change
 * - Search functionality within filter options
 * - Preloading of options when dropdown opens
 * 
 * @param {Object} options - Hook options
 * @param {Array} options.filters - Current applied filters
 * @param {Function} options.fetchFilterOptions - Function to fetch filter options from API
 *   Signature: (apiFilters, searchTerm) => Promise<{ [filterType]: Array<{value, label}> }>
 * @param {Function} options.convertFiltersToApiFormat - Function to convert UI filters to API format
 *   Signature: (uiFilters) => apiFilters
 * @param {Function} [options.isAsyncFilterType] - Optional function to check if a filter type is async
 *   Signature: (filterType) => boolean
 *   If not provided, all filter types are treated as async
 * @param {Object} [options.staticFilterValues] - Optional static values for non-async filter types
 *   Format: { [filterType]: Array<{value, label}> }
 * 
 * @returns {Object} - Hook return values
 * @returns {Function} returns.loadFilterOptions - Function to load options for a specific filter type
 * @returns {Function} returns.handleFilterDropdownOpen - Function to call when filter dropdown opens
 * @returns {Object} returns.filterOptionsCache - Current cached filter options
 */
export default function useAsyncFilterOptions({
  filters,
  fetchFilterOptions,
  convertFiltersToApiFormat,
  isAsyncFilterType = () => true,
  staticFilterValues = {},
}) {
  const [filterOptionsCache, setFilterOptionsCache] = useState({});
  const isFilterOptionsCacheLoaded = useRef(false);

  /**
   * Load all filter options from API
   */
  const loadAllFilterOptions = useCallback(
    async (currentFilters) => {
      try {
        const apiFilters = convertFiltersToApiFormat(currentFilters);
        const allOptions = await fetchFilterOptions(apiFilters, '');
        setFilterOptionsCache(allOptions);
        isFilterOptionsCacheLoaded.current = true;
        return allOptions;
      } catch (error) {
        console.error('Failed to load filter options:', error);
        return {};
      }
    },
    [convertFiltersToApiFormat, fetchFilterOptions],
  );

  /**
   * Load filter options for a specific filter type
   * Used by the Toolbar's AsyncSelectMulti component
   * 
   * @param {string} filterType - The type of filter to load options for
   * @param {string} inputSearchTerm - Optional search term to filter options
   * @returns {Promise<Array>} - Array of filter options
   */
  const loadFilterOptions = useCallback(
    async (filterType, inputSearchTerm = '') => {
      // For non-async filter types, return static values
      if (!isAsyncFilterType(filterType)) {
        return staticFilterValues[filterType] || [];
      }

      // If searching, always fetch fresh data with search term
      if (inputSearchTerm) {
        try {
          const apiFilters = convertFiltersToApiFormat(filters);
          const allOptions = await fetchFilterOptions(apiFilters, inputSearchTerm);
          return allOptions[filterType] || [];
        } catch (error) {
          console.error('Failed to load filter options with search:', error);
          return [];
        }
      }

      // If cache is loaded, return from cache
      if (isFilterOptionsCacheLoaded.current && filterOptionsCache[filterType]) {
        return filterOptionsCache[filterType];
      }

      // Load all options if not cached yet
      if (!isFilterOptionsCacheLoaded.current) {
        const allOptions = await loadAllFilterOptions(filters);
        return allOptions[filterType] || [];
      }

      return filterOptionsCache[filterType] || [];
    },
    [
      filterOptionsCache,
      filters,
      staticFilterValues,
      isAsyncFilterType,
      convertFiltersToApiFormat,
      fetchFilterOptions,
      loadAllFilterOptions,
    ],
  );

  // Invalidate filter options cache when applied filters change
  useEffect(() => {
    isFilterOptionsCacheLoaded.current = false;
    setFilterOptionsCache({});
  }, [filters]);

  /**
   * Handle filter dropdown open - preload filter options
   */
  const handleFilterDropdownOpen = useCallback(() => {
    if (!isFilterOptionsCacheLoaded.current) {
      loadAllFilterOptions(filters);
    }
  }, [loadAllFilterOptions, filters]);

  return {
    loadFilterOptions,
    handleFilterDropdownOpen,
    filterOptionsCache,
  };
}

